import { LocalStorageService } from '@services/local-storage.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  ActionsData,
  ActionsHeader,
  AppActions,
  BasicAppsHeader,
  FavAppsHeader,
  NewsFeedHeader,
  NewsFeedsType,
  NewsFeedSubject,
  NewsFeedSubjectOptions,
  NewsFeedTypesList,
  PinnedTemplates,
  ReportingHeader,
  ReportingSection
} from '../shared/utils/constants';
import { Router, RouterModule } from '@angular/router';
import { MenuService } from '@services/menu.service';
import { AppItem, DashboardApps, DropdownItemBadge } from '@mds/common';
import { CommonModule } from '@angular/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { CommonAppsComponent } from '../shared/common-apps/common-apps.component';
import { AppHeader } from '../shared/utils/interfaces/app-header';
import { HeaderContainerComponent } from '../shared/header-container/header-container.component';
import {
  ButtonComponent,
  ButtonToggleComponent,
  DropdownMenuComponent,
  LoaderModule,
  TemplateCardComponent
} from '@mds/ui';
import { catchError, filter, finalize, map, merge, Observable, of, retry } from 'rxjs';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { addTagAndIconToReports, getReportPath } from '@utils/reports';
import { NewUiRoute } from '@mds/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getNewsFeedActionIcon, getNewsFeedActionName, getNewsFeedImagePath } from '@utils/newsfeed';
import { GlobalDialogService } from '@services/global-dialog.service';
import { NewObjectService } from '@services/new-object.service';
import { RouterHelperService } from '@services/router-helper.service';
import { FirstUserNamePipe } from '@pipes/first-user-name.pipe';

export enum APIType {
  FAV_APPS = 1,
  FAV_TEMPLATE = 2,
  NEWS_FEEDS = 3
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    CommonModule,
    MdsIconsModule,
    CommonAppsComponent,
    HeaderContainerComponent,
    ButtonComponent,
    ButtonToggleComponent,
    DropdownMenuComponent,
    TemplateCardComponent,
    RouterModule,
    LoaderModule,
    NewUiRoute,
    FirstUserNamePipe,
  ],
  providers: [NewUiRoute],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit {
  dashboardData: DashboardApps;
  appDetails: AppHeader = BasicAppsHeader;
  actionDetails: AppHeader = ActionsHeader;
  favAppDetails: AppHeader = FavAppsHeader;
  reporting: AppHeader = ReportingHeader;
  pinnedTemplates: AppHeader = PinnedTemplates;
  newsFeeds: AppHeader = NewsFeedHeader;
  actionsData = ActionsData;
  reportApps = ReportingSection;
  favoriteTemplates = [];
  newsFeedsList = [];
  filteredNewsFeedsList = [];

  loaderForNewsFeed = true;
  loading = true;
  loaderForFavApps = true;
  loaderForFavTemplate = false;

  showAllFeeds = false;
  NewsFeedType = NewsFeedsType; // expose enum to template
  selectedNewsFeedSubject: NewsFeedSubject = NewsFeedSubject.ME;
  newsFeedSubjectOptions = NewsFeedSubjectOptions;
  currentUser: any;
  newsFeedTypes = NewsFeedTypesList;
  selectedNewsFeedTypes = NewsFeedTypesList;
  badgeOptions = {
    value: this.selectedNewsFeedTypes.length,
    size: 'large',
    color: 'primary',
    type: 'circle',
  } as unknown as DropdownItemBadge;
  protected readonly icons = MdsIcon;

  private changeDetection = inject(ChangeDetectorRef);
  private router = inject(Router);
  private menuService = inject(MenuService);
  private routerHelperService = inject(RouterHelperService);
  private globalDialogService = inject(GlobalDialogService);
  private localStorageService = inject(LocalStorageService);
  private newObjectService = inject(NewObjectService);
  private destroyRef = inject(DestroyRef);

  favoriteApps: AppItem[] = [];

  apps$: Observable<AppItem[]> = this.menuService.getApps();
  categories$ = this.menuService.getCategories();

  constructor() {
    this.currentUser = this.localStorageService.getItem('user_info');
  }

  get visibleItems() {
    return this.showAllFeeds
      ? this.filteredNewsFeedsList
      : this.filteredNewsFeedsList.slice(0, 6);
  }

  get shouldShowToggle() {
    return this.filteredNewsFeedsList.length > 6;
  }

  ngOnInit(): void {
    if (this.checkUserPlans()) {
      return;
    }

    this.menuService.clearMenuConfig();
    this.menuService.reload$
      .pipe(
        filter((reload) => !!reload),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.getAllDashboardData();
      });

    this.getAllDashboardData();

    this.menuService
      .getCachedFavoriteApps()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((apps) => {
        this.favoriteApps = apps;
        this.changeDetection.markForCheck();
      });
  }

  getAllDashboardData() {
    this.menuService
      .getAllDashboardData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: DashboardApps) => {
          this.dashboardData = res;
          this.getDataInParallel();
        },
      });
  }

  getDataInParallel() {
    this.loaderForFavApps = true;
    this.loaderForNewsFeed = true;
    this.loaderForFavTemplate = true;
    this.loading = true;

    merge(
      this.menuService
        .getSectionFromDashboardSection(this.dashboardData.apps.endpoint)
        .pipe(
          finalize(() => {
            this.loaderForFavApps = false;
          }),
          retry(3),
          map((res) => ({ source: APIType.FAV_APPS, data: res.apps })),
          catchError((error) =>
            of({ source: APIType.FAV_APPS, error, failed: true })
          )
        ),
      this.menuService
        .getSectionFromDashboardSection(this.dashboardData.templates.endpoint)
        .pipe(
          finalize(() => {
            this.loaderForFavTemplate = false;
          }),
          retry(3),
          map((res) => ({ source: APIType.FAV_TEMPLATE, data: res })),
          catchError((error) =>
            of({ source: APIType.FAV_TEMPLATE, error, failed: true })
          )
        ),
      this.menuService
        .getSectionFromDashboardSection(this.dashboardData.newsfeed.endpoint)
        .pipe(
          finalize(() => {
            this.loaderForNewsFeed = false;
          }),
          retry(3),
          map((res) => ({ source: APIType.NEWS_FEEDS, data: res })),
          catchError((error) =>
            of({ source: APIType.NEWS_FEEDS, error, failed: true })
          )
        )
    ).subscribe({
      next: (result) => {
        this.loading = false;
        if (!this.isErrorResponse(result)) {
          switch (result.source) {
            case APIType.FAV_APPS:
              break;

            case APIType.FAV_TEMPLATE:
              this.favoriteTemplates = result.data.templates?.reverse();
              this.favoriteTemplates = addTagAndIconToReports(
                this.favoriteTemplates
              );
              this.changeDetection.markForCheck();
              break;

            case APIType.NEWS_FEEDS:
              this.newsFeedsList = result.data.newsfeed || [];
              this.filterNewsFeeds();
              this.changeDetection.markForCheck();
              break;
            default:
              break;
          }
        }
      },
    });
  }

  onAppData(app: { name: string }) {
    this.routerHelperService.openAppsDialog(app.name);
  }

  onReportAppsClicked(actionApp: any) {
    switch (actionApp.action) {
      case AppActions.PIVOT_MANAGER:
        this.navigateToRoute('/report');
        break;
      case AppActions.REPORT_HISTORY:
        this.router.navigate(['/reports/history']);
        break;
      default:
        break;
    }
  }

  toggleShow() {
    this.showAllFeeds = !this.showAllFeeds;
  }

  doAction(actionApp: { action: AppActions }) {
    switch (actionApp.action) {
      case AppActions.IMPORTER:
        this.openImportApps();
        break;

      case AppActions.ADD_DATA:
        this.newObjectService.createNewObjectDialog();
        break;
      default:
        break;
    }
  }

  openImportApps() {
    this.globalDialogService.openImportApps();
  }

  openAppModal(app?: AppItem | string): void {
    this.globalDialogService.openApp(app as AppItem);
  }

  onNewsFeedSubjectChange(sectionChange: MatButtonToggleChange) {
    this.selectedNewsFeedSubject = sectionChange.value;
    this.filterNewsFeeds();
  }

  onImageError(event: Event, news: any) {
    const target = event.target as HTMLImageElement;
    target.src = getNewsFeedImagePath(news);
  }

  newsFeedAction(news: any) {
    let url = '';
    switch (news.feed_type) {
      case NewsFeedsType.TEAM_MEMBER_NEWS:
        url = decodeURIComponent(news.users[0].url);
        break;

      case NewsFeedsType.REPORT_NEWS:
        url = decodeURIComponent(news.importlink[0].url);
        break;

      case NewsFeedsType.IMPORT_COMPONENT:
      case NewsFeedsType.PRODUCT_ANALYSIS:
        let rid = news.product.url.split('/').pop();
        rid = rid.replace('#', '');
        url = `mattermaps/overview/${rid}/edit`;
        break;

      case NewsFeedsType.SHARING_NEWS:
        let _rid = news.product[0].url.split('/').pop();
        _rid = _rid.replace('#', '');
        url = `mattermaps/overview/${_rid}/edit`;
        break;
      default:
        break;
    }

    this.navigateToRoute(url);
  }

  onFeedTypeSelection(data: any) {
    this.selectedNewsFeedTypes = data;
    this.filterNewsFeeds();
  }

  private filterNewsFeeds() {
    const feedTypes = this.selectedNewsFeedTypes.map((obj) => obj.value);

    const listBasedOnFeedTypes = this.newsFeedsList.filter((item) =>
      feedTypes.includes(item.feed_type)
    );

    if (this.selectedNewsFeedSubject === NewsFeedSubject.ME) {
      const filtered = listBasedOnFeedTypes.filter((item) => {
        if (Array.isArray(item.users)) {
          const foundItem = item.users.some(
            (user) =>
              user.url.split('/').pop() ===
              this.currentUser.rid.replace('#', '')
          );
          return foundItem;
        }
        if (item.users) {
          return item.users.rid === this.currentUser.rid;
        }
        return false;
      });
      this.filteredNewsFeedsList = filtered;
    } else {
      this.filteredNewsFeedsList = listBasedOnFeedTypes;
    }
  }

  configureFavApps() {
    this.menuService
      .getFavoriteApps()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((apps) => {
        this.favoriteApps = apps;
        this.changeDetection.markForCheck();
      });
  }

  // TODO clean up this you shouldn't bind from function in html

  openLinkProductName(link: string) {
    return link.replace('#', '');
  }

  onTemplateMenuOptionClicked(event: any, template: any) {}

  getReportLink(isTemplate: boolean, template: any) {
    return getReportPath(
      isTemplate,
      template.original_rid,
      isTemplate ? { id: template.original_rid.replace('#', '') } : null
    );
  }
  getImagePath(news: any) {
    return getNewsFeedImagePath(news);
  }

  getNewsFeedActionIcon(news: any) {
    return getNewsFeedActionIcon(news);
  }

  getNewsFeedActionName(news: any) {
    return getNewsFeedActionName(news);
  }

  private checkUserPlans() {
    const userInfo = JSON.parse(localStorage.getItem('user_info'));
    const plans = (userInfo?.features ?? []).map(
      (feature: { plan: string }) => feature.plan
    );
    const isUserExternalSupplier = plans.includes('external_supplier');
    const hasScope3Plan = plans.includes('scope3_dashboard');
    if (isUserExternalSupplier) {
      this.router.navigate(['/information-requests/supplier']);
      return true;
    }
    if (hasScope3Plan) {
      this.router.navigate(['/scope-3']);
      return true;
    }
    return false;
  }

  private navigateToRoute(route: string) {
    this.router.navigateByUrl(route);
  }

  private isErrorResponse(
    response: any
  ): response is { source: APIType; error: any; failed: boolean } {
    return response && typeof response.failed === 'boolean';
  }
}
